import PropTypes from 'prop-types';
import _ from 'lodash';

import React, { useState } from 'react';
import {
  Heading,
  Text,
} from 'rebass';
import { Flex, Box } from 'reflexbox';
import ReactMarkdown from 'react-markdown';

import { format } from 'date-fns';

import SharedConfig from 'shared/config';

import config from 'frontend/config';
import {
  DarkerBox,
  If,
} from 'frontend/components';

/* eslint-disable max-len */
const updates = [{
  date: new Date(2024, 3, 23),
  title: 'Raidbots is updated for Season 4',
  body: `
* Droptimizer
  * All 3 raids are available and have new Awakened item level tiers and new tier sets.
  * Mythic+ and Dungeon sources have been updated for the new  item level adjustments.
  * PVP Season 4 added.
  * Season 4 Raid Vendor source added.
  * Profession items updated with new Awakened Crest upgrades.
* Gear Compare moved to Legacy section. Top Gear item search is a better tool in most cases.
`,
}, {
  date: new Date(2024, 0, 19),
  title: 'Item Search now available in Top Gear',
  body: `
Top Gear now has an Item Search section that will let you add arbitrary items without needing
to dig into item strings.

Please report any bugs or feedback in the Raidbots Discord.
`,
}, {
  date: new Date(2023, 10, 7),
  title: 'Raidbots is updated for 10.2: Guardians of the Dream',
  body: `
* Top Gear
  * Helm and waist enchants have been added
  * Updated for new sets, catalyst conversions, and item upgrade currencies
* Droptimizer
  * Added source for Amirdrassil
  * Added the new world boss
  * Added the new profession upgrade items
`,
}, {
  date: new Date(2023, 8, 5),
  title: 'Raidbots is updated for 10.1.7: Fury Incarnate',
  body: `
* Droptimizer
  * Dreamsurge gear has been added.
  * Heroic Dawn of the Infinite item levels are supported.
`,
}, {
  date: new Date(2023, 6, 11),
  title: 'Raidbots is initially updated for 10.1.5: Fractures in Time',
  body: `
### Augmentation Evokers will not be supported on Raidbots for the 10.1.5 launch

SimC folks are still working on making it all work properly. It's specifically difficult as it requires simming additional raid members which is significantly slower than other classes/specs. In addition, the spec is not currently compatible with the SimC technique that Raidbots uses for almost all tools (profilesets).

SimC is entirely developed by volunteers so **there is no ETA** on when that work will be finished or when it will be available on Raidbots.

Evoker Discord will be a good place for up-to-date info on gearing/talent suggestions. There will also be guides on the usual website - I'll edit this post with links when they are available.

### 10.1.5 Trinkets and Weapon Proc Sims are Incomplete

Blizzard did not provide a trinket/weapon vendor on the 10.1.5 PTR so SimC devs were not able to test many of the trinkets/weapons from the new megadungeon and the solo Time Rifts content.

Some of them have prospective implementations but they probably won't be fully understood/tested for a week or two.

The [SimulationCraft Dragonflight 10.1.5 spreadsheet](https://docs.google.com/spreadsheets/d/e/2PACX-1vSCfqvYso2z8Ir8LZ3NhRYn5vcQXaTAOAi9GbY_hhjnl02L-QKMX1K_c9kyXRLXuGE08dl0wBLRzF-C/pubhtml) is fairly up-to-date with  more detailed info.

### Detailed Release Notes

* Top Gear
  * "Add socket (Great Vault)" is now available in the + menu. This lets you add a socket to an item that will not be
    simmed along with other Vault gear to determine if it's a better value than the items.
* Droptimizer
  * Dawn of the Infinite has been added
* Gear Compare
  * Items from previous expansions now show that information
* Simulation Options
  * Added Power Infusion
* Premium
  * Many new payment methods have been added:
    * ACH Bank Transfer (US)
    * SOFORT (EU)
    * iDEAL (EU)
    * Bancontact (EU)
  * Patreon support is being phased out and will no longer work in Jan 2024
* General
  * Replaced Google Analytics with plausible.io (significantly more privacy focused)
  `,
}, {
  date: new Date(2023, 4, 6),
  title: 'Droptimizer now has additional raid item level choices to evaluate various upgrade levels',
}, {
  date: new Date(2023, 4, 2),
  title: 'Raidbots is updated for 10.1: Embers of Neltharion',
  readMoreText: 'Read the Raidbots 10.1 Patch Notes',
  body: `
* Top Gear
  * Full support for the new item upgrade system has been implemented.
    * Item Upgrade Currency panel will automatically import the relevant currencies and items when using the latest
      version of the SimulationCraft addon.
    * You can test out which items to upgrade by using the + menu on individual items. Top Gear will only run
      combinations that you can afford.
    * Recrafting upgrades that require Enchanted Crests will intelligently use both the Enchanted Crests and the base
      crests.
    * Top Gear properly handles the cost discounts based on highest slot item level on your main and alts if you are
      using the most recent version of the SimulationCraft addon.
  * Items can be recrafted at higher quality levels using the + menu.
  * The Item Sets panel has been redesigned to show all available item sets and let you choose which set bonuses to
    always include.
  * The new Leatherworking leg enchant has been added.
* Droptimizer
  * Aberrus, the Shadowed Crucible has been added.
    * All tier tokens, catalyst transformations, and the endboss omnitoken are supported.
  * Mythic+ has been updated with the new dungeon rotation.
  * The new World Boss has been added to the &quot;Dragon Isles&quot; source.
  * Inspiration Catalyst has been added for the new tier catalyst.
  * The new upgrade items (Sparks and Crests) have been added to the "Professions - Epic" source.
* Simulation Options
  * Options added for the Ominous Chromatic Essence trinket and the Iced Phial of Corrupting Rage.
  * Trinkets from the previous season have been moved under "Show More Options"
  `,
}, {
  date: new Date(2023, 2, 21),
  title: 'Raidbots is updated for 10.0.7',
  body: `
Primordial Stones are being simulated in SimulationCraft.

* Gear Compare
  * Primordial Gems can be chosen and simmed in the Onyx Annulet.
  * Engineering Tinkers are now available
* Droptimizer
  * Primal Storm source has been updated with new 395 upgrade level gear.

**Note:** There are currently no plans to add a UI for Primordial Stones to Top Gear because it would require
significant technical and interface complexity for a short-lived system.
  `,
}, {
  date: new Date(2023, 0, 22),
  title: 'Revival Catalyst is now supported in Droptimizer and Top Gear',
  body: `
* Top Gear
  * Convert valid items to the catalyst version using the Copy and Modify menu on an item.
  * Choose how many charges you have available in the Catalyst Charges panel.
* Droptimizer
  * For seasonal content (raids, M+, PVP, etc), the Catalyst converted version of an item will be included.
  * Revival Catalyst has been added as a source
  `,
}, {
  date: new Date(2023, 0, 6),
  title: `
  Droptimizer can now sim all usable items even if it does not drop for your spec (Chillglobe for casters,
  daggers for Outlaw, etc). Enable the "Include Off-Spec Items" Checkbox for this functionality.`
  ,
}, {
  date: new Date(2022, 11, 5),
  title: 'Profession items are now available in Droptimizer',
}, {
  date: new Date(2022, 10, 28),
  title: 'Raidbots is updated for Dragonflight Expansion Launch!',
  readMoreText: 'Read the Raidbots Patch Notes',
  body: `
# Raidbots Dragonflight Prepatch Patch Notes

* **Most UI options have been reset!** Double check your consumable and gem/enchant settings.
* Top Gear
  * **Functionality change!** Top Gear will only test gem combinations of 1 type of Primalist gem and 1 type of normal gem.
    For example - if you have 5 sockets, Top Gear will only test gem combinations that have 1 primalist gem and 4 of
    the same type of secondary gem. It will no longer mix and match the secondary gems in those 4 open slots.
    * _Gem sims have been thorny for a while - they take forever, cause UI slowdowns, and often don't provide
      useful info because they are such small DPS changes. My hope is that the simpler approach reduces some of the
      pain points without dramatically impacting recommendations._
  * Added Dragonflight gems and enchants
  * There is now a limit on how many gems/enchants you can select at once to prevent needlessly slow sims and UI slowdowns.
  * Improved performance when a lot of gems/enchants are selected.
* Droptimizer
  * Added Dragonflight world bosses, raid, M+ dungeons, and PVP gear
  * Remove Shadowlands sources
* Simulation Options
  * Added Dragonflight potions, food, flasks, augmentations, and weapon oils to Simulation Options
  * Added option for Whispering Incarnate Icon trinket
  * Removed "Iterations per Actor" option. Raidbots will always use Smart Sim.
* Developers
  * Talent Tree iframe embeds are now available.
  * Added Enchantments JSON to static data. This file collects extensive info on gems and enchants.
  * Talents JSON now contains data for talent trees.
  `,
}, {
  date: new Date(2022, 9, 25),
  title: 'Raidbots is updated for Dragonflight Prepatch!',
  readMoreText: 'Read the Raidbots Patch Notes',
  body: `
# Raidbots Dragonflight Prepatch Patch Notes

* The new talent trees are here! Talents are now shown using the new tree and you can export any talents to be used in-game.
* Top Gear can quickly sim any of your saved loadouts or you can use the (beta!) Generate Combinations. Note that talent
  tree combinations are HUUUUUUUGE and so you will likely need to fill out most of the tree in order to fit within
  the Top Gear iteration limit.
* Armory import is currently disabled until I can implement the new Blizzard API.
* Grab the latest version of the SimC addon to handle the new talent trees.
* Mark of the Wild has been added to Simulation Options.
* Shadowlands features (soulbinds, conduits, etc) have been removed.
* Shadowland trinket/soulbind options have been removed from Simulation Options.
* Reports will warn if you are using edited/custom input rather than direct-from-the-addon input.
* Talent Compare removed - all talent functionality is now handled in Top Gear.
* Stat Weights removed from primary navigation. They are an outdated tool that generally shouldn't be used.
  `,
}, {
  date: new Date(2022, 7, 16),
  title: `Droptimizer now includes tier gear obtainable via the Creation Catalyst when simming eligible sources
  (fated raids, M+, etc)`,
}, {
  date: new Date(2022, 6, 16),
  title: 'Top Gear now has support for Item Upgrades.',
  body: `
Everybody cool is playing the Dragonflight Alpha while I'm over here finally implementing features that came out in 9.0.5!

Item Upgrade levels are now supported in Top Gear! M+ and PVP items will have new options in the "Copy and Convert to..." menu (the little plus icon in item selection) which will create a copy of your item at the higher upgrade level.

You can use the "Upgrade Budget" options above the gear list to set how much currency you can spend at one time - this lets you test out a bunch of different items while staying within the amount of currency you currently have (or will have). Top Gear will skip combinations that cost too much so you don't end up with the obvious-but-useless answer of "upgrade all your items to the top level"
  `,
}];
/* eslint-enable max-len */

const updatesDecorated = _.orderBy(
  _.map(updates, update => ({
    ...update,
    body: _.trim(update.body),
    unixTime: update.date.getTime(),
  })),
  'unixTime',
  'desc',
);

function NewsItem({ title, date, body, readMoreText }) {
  const [open, setOpen] = useState(false);

  return (
    <Box style={{ backgroundColor: '#191919', borderRadius: 10, padding: 10 }}>
      <Box
        onClick={() => setOpen(!open)}
        style={{
          cursor: body ? 'pointer' : null,
        }}
      >
        <Flex align="center">
          <Text bold size={18} style={{ whiteSpace: 'nowrap' }} color={config.colors.blue}>
            {format(date, 'MMM do, y')}
          </Text>
          <Text ml={1} size={18}>{title}</Text>
          <If
            condition={body}
            then={() => (
              <Text ml={1} bold>
                {/* eslint-disable-next-line */}
                <a style={{ cursor: 'pointer' }}>{readMoreText}</a>
              </Text>
            )}
          />
        </Flex>
      </Box>
      <If
        condition={body && open}
        then={() => (
          <Box pl={1}>
            <ReactMarkdown>
              {body}
            </ReactMarkdown>
          </Box>
        )}
      />
    </Box>
  );
}
NewsItem.propTypes = {
  title: PropTypes.string.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  body: PropTypes.string,
  readMoreText: PropTypes.string,
};

NewsItem.defaultProps = {
  readMoreText: 'Read More',
  body: null,
};

const noFutureItems = _.filter(updatesDecorated, item => item.unixTime < Date.now());

const ITEMS_TO_SHOW = 5;
const totalItems = noFutureItems.length;

function News() {
  const [itemsToShow, setItemsToShow] = useState(ITEMS_TO_SHOW);
  return (
    <DarkerBox col={12}>
      <Heading level={3} mb={1}>
        Site Updates
      </Heading>
      {_.map(_.take(noFutureItems, itemsToShow), update => (
        <Box mb={2} key={update.unixTime}>
          <NewsItem
            title={update.title}
            date={update.date}
            body={update.body}
            readMoreText={update.readMoreText}
          />
        </Box>
      ))}
      <If
        condition={itemsToShow < totalItems}
        then={() => (
          <Text ml={1} bold onClick={() => setItemsToShow(itemsToShow + 5)}>
            {/* eslint-disable-next-line */}
            <a style={{ cursor: 'pointer' }}>Older Updates</a>
          </Text>
        )}
      />
      <Text mt={2}>
        For more detailed site updates, join the
        {' '}
        <a
          href={SharedConfig.links.discord}
          target="_blank"
          rel="noopener noreferrer"
        >
          Raidbots Discord
        </a>.
      </Text>
    </DarkerBox>
  );
}

export default News;
